import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { faqItems, siteConfig } from '@/mocks/data';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const section1 = useScrollReveal();

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title="常見問題｜樂律 Lelyric"
        description="查看樂律品牌常見問題，包含品牌介紹、書籍查詢、購書通路及合作洽詢等相關資訊。"
        canonicalPath="/faq"
        structuredData={faqSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[100svh] flex items-center bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/01a0ec79-0020-7903-b224-53a0ca081d56.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute top-6 right-6 md:top-10 md:right-14 select-none pointer-events-none">
            <span className="font-heading text-5xl md:text-7xl font-bold text-background-50/[0.03]">04</span>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
            <div className="max-w-4xl mx-auto text-center">

              <h1 className="font-heading text-[3rem] md:text-[4rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-4">
                常見問題
              </h1>
              <p className="text-base md:text-lg text-background-50/50 max-w-xl mx-auto leading-relaxed font-light tracking-wide">
                以下為樂律品牌相關常見問題。如需更多資訊，請參考合作通路頁面或前往崧博出版官網。
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
        </section>

        {/* FAQ Content */}
        <section className="w-full min-h-[70svh] flex items-start px-6 md:px-10 py-16 md:py-24 bg-gradient-to-b from-primary-50/70 via-background-50 to-background-50" ref={section1.ref}>
          <div className={`w-full max-w-3xl mx-auto transition-all duration-700 ease-out ${section1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex flex-col gap-4">
              {faqItems.map((item, i) => {
                const isOpen = openIndex === i;
                return (
                  <div
                    key={i}
                    className={`rounded-lg border transition-all duration-500 ease-out delay-[${i * 80}ms] ${
                      section1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    } ${
                      isOpen
                        ? 'border-primary-300/60 bg-background-50'
                        : 'border-background-200/70 bg-background-100 hover:border-background-300/60'
                    }`}
                  >
                    <button
                      onClick={() => toggleFaq(i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${i}`}
                    >
                      <span className="font-heading text-lg font-semibold text-foreground-900 pr-4 group-hover:text-primary-600 transition-colors">
                        {item.question}
                      </span>
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isOpen ? 'bg-primary-100 rotate-45' : 'bg-background-200/70 group-hover:bg-background-300/60'
                      }`}>
                        <i className={`text-base ${isOpen ? 'text-primary-600' : 'text-foreground-500'} ri-add-line`}></i>
                      </span>
                    </button>
                    <div
                      id={`faq-answer-${i}`}
                      className={`overflow-hidden transition-all duration-400 ease-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-6 pb-5">
                        <div className="w-full h-px bg-background-200/70 mb-4"></div>
                        <p className="text-base md:text-lg text-foreground-600 leading-relaxed text-left">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom CTAs */}
            <div className="mt-14 pt-10 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={siteConfig.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
              >
                前往書籍資料庫
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
              <a
                href={siteConfig.sonbookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium border border-foreground-200 text-foreground-700 hover:border-foreground-400 hover:bg-background-100 transition-all cursor-pointer"
              >
                前往崧博出版
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}